import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import packageJson from '../../package.json';
import './ChatApp.css';
import '../App.css';
import '../styles/VerticalScrollSelector.css';

// Hooks
import { useAuthContext } from "../hooks/AuthContext";
import { useAdminAccess } from "../hooks/AdminAccessContext";
import { useWebRTC } from '../hooks/useWebRTC';
import { useAudioControls } from '../hooks/useAudioControls';
import { useUserPreferences } from '../hooks/useUserPreferences';
import { usePwaInstallPrompt } from '../hooks/usePwaInstallPrompt';

// Components
import IntroController from './Intro/IntroController';
import LoadingScreen from './common/LoadingScreen';
import GameHeader from './common/GameHeader';
import GameSetupScreen from './GameSetupScreen';
import CreditStoreModal from './CreditStoreModal';
import CurrentQuestion from './CurrentQuestion';
import ConversationManager from './conversation/ConversationManager';
import AudioControls from './audio/AudioControls';
import GameControls from './GameControls';
import ScoreBoard from './common/ScoreBoard';
import SettingsManager from './SettingsManager';
import ConfirmationDialog from './common/ConfirmationDialog';
import IdleWarningModal from './common/IdleWarningModal';
import InstallPromptModal from './InstallPromptModal';
import PulsatingHead from "../PulsatingHead";
import { GameStateProvider, useGameStateContext } from '../contexts/GameStateProvider';
import { readActiveGameSessionSnapshot } from '../services/activeGameSessionStorage';
import { getHostImageSrc } from '../utils/hostImages';
import { getVisibleGamePacksForSetup } from './packSelectorUtils';
import {
  getInstallPromptVariant,
  markInstallPromptDismissed,
  markInstallPromptInstalled,
  markInstallPromptShown,
  shouldShowInstallPrompt,
} from '../utils/installPrompt';

const CHECKOUT_WALLET_REFRESH_ATTEMPTS = 6;
const CHECKOUT_WALLET_REFRESH_DELAY_MS = 1500;
const PENDING_CHECKOUT_SELECTION_KEY = 'cq_pending_checkout_selection';

const wait = (delayMs) => new Promise((resolve) => {
  setTimeout(resolve, delayMs);
});

const confirmCheckoutSession = async (checkoutSessionId) => {
  const token = localStorage.getItem('auth_token');

  if (!token || !checkoutSessionId) {
    return false;
  }

  const response = await fetch('/.netlify/functions/confirm-checkout-session', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ checkoutSessionId }),
  });
  const data = await response.json();

  if (!response.ok || data?.success === false) {
    throw new Error(data?.message || data?.error || 'Failed to confirm purchase.');
  }

  return true;
};

const readPendingCheckoutSelection = () => {
  try {
    const storedSelection = window.localStorage.getItem(PENDING_CHECKOUT_SELECTION_KEY);
    return storedSelection ? JSON.parse(storedSelection) : null;
  } catch (error) {
    return null;
  }
};

const clearPendingCheckoutSelection = () => {
  window.localStorage.removeItem(PENDING_CHECKOUT_SELECTION_KEY);
};

const ChatApp = ({
  embedded = false,
  embeddedLaunchSpec = null,
  onEmbeddedSessionClosed = null,
}) => {
  const { authenticatedUser, signOut } = useAuthContext();
  const { isAdmin } = useAdminAccess();
  const [isSigningOut, setIsSigningOut] = useState(false);
  const activeGameSnapshotOnLoadRef = useRef(undefined);

  if (activeGameSnapshotOnLoadRef.current === undefined) {
    activeGameSnapshotOnLoadRef.current = readActiveGameSessionSnapshot();
  }

  // Client info for logging
  const clientInfo = {
    appVersion: packageJson.version,
    reactVersion: packageJson.dependencies.react,
    supabaseDetails: packageJson.dependencies.supabase,
    language: navigator.language,
  };

  // Initialize user preferences hook
  const userPreferences = useUserPreferences({ authenticatedUser });

  const handleSignOut = useCallback(async () => {
    try {
      setIsSigningOut(true);
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error('Unable to sign out right now.');
    } finally {
      setIsSigningOut(false);
    }
  }, [signOut]);

  // Show loading screen while preferences are being loaded
  if (!userPreferences.hasLoadedInitialPreferences) {
    return <LoadingScreen />;
  }

  // Show intro if user preference is set
  if (!embedded && userPreferences.shouldShowIntroOnLoad && !activeGameSnapshotOnLoadRef.current) {
    return (
      <IntroController
        onFinish={() => {
          userPreferences.setShouldShowIntroOnLoad(false);
        }}
        onHideIntro={async () => {
          await userPreferences.updateShowIntroPreference(false);
        }}
      />
    );
  }

  return (
    <GameStateProvider
      authenticatedUser={authenticatedUser}
      clientInfo={clientInfo}
      onUsageUpdate={(usageData) => {
        // console.log("💰 Updated usage totals from provider:", usageData);
      }}
      initializeWebRTCConnection={(token, conversation) => {
        // This will be handled by the WebRTC hook in ChatAppWithContext
        return Promise.resolve();
      }}
      conversationMode={userPreferences.conversationMode}
      isAdmin={isAdmin}
    >
      <ChatAppWithGameState
        authenticatedUser={authenticatedUser}
        userPreferences={userPreferences}
        isAdmin={isAdmin}
        clientInfo={clientInfo}
        onSignOut={handleSignOut}
        signOutLoading={isSigningOut}
        embedded={embedded}
        embeddedLaunchSpec={embeddedLaunchSpec}
        onEmbeddedSessionClosed={onEmbeddedSessionClosed}
      />
    </GameStateProvider>
  );
};



// Component that has access to GameState context
const ChatAppWithGameState = ({
  authenticatedUser,
  userPreferences,
  isAdmin,
  clientInfo,
  onSignOut,
  signOutLoading,
  embedded = false,
  embeddedLaunchSpec = null,
  onEmbeddedSessionClosed = null,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const creatorStudioLaunchHandledRef = useRef('');
  const gameState = useGameStateContext();
  const {
    conversationStarted,
    gamePacks,
    loading: gameStateLoading,
    selectedGamePack,
    setSelectedGamePack,
    setSelectedPack,
  } = gameState;
  const {
    conversationMode,
    setConversationMode,
  } = userPreferences;
  const endConversation = gameState.endConversation;
  const finalizeGameSession = gameState.finalizeGameSession;
  const fetchWallet = gameState.fetchWallet;
  const sendActiveGameSessionHeartbeat = gameState.sendActiveGameSessionHeartbeat;
  const availableGamePacks = getVisibleGamePacksForSetup(gameState.gamePacks, { embedded });
  const currentHostImageSrc = getHostImageSrc(gameState.selectedGamePackDetails?.host_image_filename);
  const currentHostName = gameState.selectedGamePackDetails?.host_name || 'Host';
  const currentQuestion = gameState.currentQuestion || { question: '', round: null };
  const scoreBoardPlayers = gameState.players || {};

  // UI state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showCreditStore, setShowCreditStore] = useState(false);
  const [idleWarningVisible, setIdleWarningVisible] = useState(false);
  const [idleCountdownSeconds, setIdleCountdownSeconds] = useState(0);
  const [installPromptQueued, setInstallPromptQueued] = useState(false);
  const [installPromptVariant, setInstallPromptVariant] = useState(null);
  const [questionBoxHeight, setQuestionBoxHeight] = useState(0);
  const [remoteAudioVolume, setRemoteAudioVolume] = useState(0);
  const [micVolume, setMicVolume] = useState(0);
  const conversationStartedRef = useRef(false);
  const showConfirmDialogRef = useRef(false);
  // Mirror remoteAudioVolume into a ref so async waits see live updates
  const remoteAudioVolumeRef = useRef(0);
  useEffect(() => {
    remoteAudioVolumeRef.current = remoteAudioVolume;
  }, [remoteAudioVolume]);
  useEffect(() => {
    conversationStartedRef.current = gameState.conversationStarted;
  }, [gameState.conversationStarted]);
  useEffect(() => {
    showConfirmDialogRef.current = showConfirmDialog;
  }, [showConfirmDialog]);
  const creditBundles = (gameState.wallet?.storeItems || [])
    .filter((item) => item.item_type === 'credit_bundle')
    .sort((left, right) => (left.credit_amount || 0) - (right.credit_amount || 0));

  // Track pending AI-triggered end_game to delay RTC teardown
  const pendingEndGameRef = useRef(false);
  const endGameTimerRef = useRef(null);
  const idleWarningTimerRef = useRef(null);
  const idleAutoEndTimerRef = useRef(null);
  const idleCountdownIntervalRef = useRef(null);
  const idleTimeoutHandlerRef = useRef(null);
  const finalizeEndGameRef = useRef(null);
  const maybeQueueInstallPromptRef = useRef(null);
  const activeSessionRestoreAttemptedRef = useRef(false);
  const lifecycleResumeInFlightRef = useRef(false);
  const micActivityRef = useRef(false);
  const idleWarningSeconds = Number(gameState.sessionLimits?.idle_warning_seconds) || 120;
  const idleAutoEndSeconds = Number(gameState.sessionLimits?.idle_auto_end_seconds) || 30;

  // Cleanup any pending timers on unmount
  useEffect(() => {
    return () => {
      if (endGameTimerRef.current) {
        clearTimeout(endGameTimerRef.current);
        endGameTimerRef.current = null;
      }
      pendingEndGameRef.current = false;
    };
  }, []);

  const clearIdleTimers = useCallback(() => {
    if (idleWarningTimerRef.current) {
      clearTimeout(idleWarningTimerRef.current);
      idleWarningTimerRef.current = null;
    }

    if (idleAutoEndTimerRef.current) {
      clearTimeout(idleAutoEndTimerRef.current);
      idleAutoEndTimerRef.current = null;
    }

    if (idleCountdownIntervalRef.current) {
      clearInterval(idleCountdownIntervalRef.current);
      idleCountdownIntervalRef.current = null;
    }
  }, []);

  const playIdleWarningTone = useCallback(() => {
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) {
        return;
      }

      const audioContext = new AudioContextClass();
      if (audioContext.state === 'suspended') {
        audioContext.resume().catch(() => {});
      }

      [0, 0.32].forEach((offset) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(880, audioContext.currentTime + offset);
        gainNode.gain.setValueAtTime(0.0001, audioContext.currentTime + offset);
        gainNode.gain.exponentialRampToValueAtTime(0.08, audioContext.currentTime + offset + 0.02);
        gainNode.gain.exponentialRampToValueAtTime(0.0001, audioContext.currentTime + offset + 0.18);

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        oscillator.start(audioContext.currentTime + offset);
        oscillator.stop(audioContext.currentTime + offset + 0.2);
      });

      setTimeout(() => {
        audioContext.close().catch(() => {});
      }, 1000);
    } catch (error) {
      console.warn('Idle warning tone failed:', error);
    }
  }, []);

  // Finalizes end game: stops RTC, ends conversation, finalizes billing
  const finalizeEndGame = async (reason = 'completed') => {
    try {
      await webRTCRef.current?.cleanup?.();

      // End the conversation through game state (this should be last)
      await endConversation({ skipFinalize: true });
      // Finalize billing with precise ai reply count
      try {
        const aiCount = typeof webRTC.getAiReplyCount === 'function' ? webRTC.getAiReplyCount() : 0;
        await finalizeGameSession(reason, aiCount);
      } catch (e) { /* non-fatal */ }

      if (reason === 'completed') {
        maybeQueueInstallPromptRef.current?.();
      }
    } catch (error) {
      console.error("❌ Error during WebRTC cleanup:", error);
    }
  };

  useEffect(() => {
    finalizeEndGameRef.current = finalizeEndGame;
  });

  useEffect(() => {
    idleTimeoutHandlerRef.current = async () => {
      if (!gameState.conversationStarted) {
        return;
      }

      clearIdleTimers();
      setIdleWarningVisible(false);
      setIdleCountdownSeconds(0);

      if (endGameTimerRef.current) {
        clearTimeout(endGameTimerRef.current);
        endGameTimerRef.current = null;
      }
      pendingEndGameRef.current = false;

      toast.info("Game ended due to inactivity.");
      await finalizeEndGameRef.current?.('idle_timeout');
    };
  }, [clearIdleTimers, gameState.conversationStarted]);

  const scheduleIdleWarning = useCallback(() => {
    if (!gameState.conversationStarted) {
      return;
    }

    clearIdleTimers();
    setIdleWarningVisible(false);
    setIdleCountdownSeconds(0);

    idleWarningTimerRef.current = setTimeout(() => {
      const deadline = Date.now() + (idleAutoEndSeconds * 1000);

      setIdleWarningVisible(true);
      setIdleCountdownSeconds(idleAutoEndSeconds);
      playIdleWarningTone();

      idleAutoEndTimerRef.current = setTimeout(() => {
        idleTimeoutHandlerRef.current?.();
      }, idleAutoEndSeconds * 1000);

      idleCountdownIntervalRef.current = setInterval(() => {
        const nextCountdown = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
        setIdleCountdownSeconds(nextCountdown);

        if (nextCountdown <= 0 && idleCountdownIntervalRef.current) {
          clearInterval(idleCountdownIntervalRef.current);
          idleCountdownIntervalRef.current = null;
        }
      }, 250);
    }, idleWarningSeconds * 1000);
  }, [
    clearIdleTimers,
    gameState.conversationStarted,
    idleAutoEndSeconds,
    idleWarningSeconds,
    playIdleWarningTone,
  ]);

  const recordUserActivity = useCallback(() => {
    if (!gameState.conversationStarted || idleWarningVisible) {
      return;
    }

    scheduleIdleWarning();
  }, [gameState.conversationStarted, idleWarningVisible, scheduleIdleWarning]);

  const handleIdleWarningContinue = useCallback(() => {
    if (!gameState.conversationStarted) {
      return;
    }

    scheduleIdleWarning();
  }, [gameState.conversationStarted, scheduleIdleWarning]);

  // Enhanced message handler that uses context methods
  const enhancedHandleWebRTCMessage = async (event) => {
    try {
      const responseData = JSON.parse(event.data);
      gameState.handleRealtimeConversationEvent(responseData);

      // Increment message count once per completed assistant response
      if (responseData.type === "response.done") {
        gameState.setMessageCount((prev) => prev + 1);
        // If an end_game was requested, defer RTC teardown until after completion + buffer
        if (pendingEndGameRef.current && !endGameTimerRef.current) {
          // Use wait‑until‑quiet strategy: min 2s, then quiet for 600ms, cap 5s
          endGameTimerRef.current = setTimeout(async () => {
            try {
              const MIN_MS = 2000;
              const CAP_MS = 5000;
              const QUIET_THRESHOLD = 6;
              const QUIET_STREAK_MS = 600;
              const start = Date.now();
              let quietStart = 0;
              const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

              // ensure minimum wait
              while (Date.now() - start < MIN_MS) {
                await sleep(100);
              }
              // then wait for quiet or cap
              while (Date.now() - start < CAP_MS) {
                const vol = remoteAudioVolumeRef.current || 0;
                if (vol <= QUIET_THRESHOLD) {
                  if (quietStart === 0) quietStart = Date.now();
                  if (Date.now() - quietStart >= QUIET_STREAK_MS) break;
                } else {
                  quietStart = 0;
                }
                await sleep(120);
              }

              toast.success("Game ended. Thanks for playing Cringe Quest!");
              await finalizeEndGame('completed');
            } finally {
              pendingEndGameRef.current = false;
              clearTimeout(endGameTimerRef.current);
              endGameTimerRef.current = null;
            }
          }, 0);
        }
      }

      // Handle function calls for score updates
      if (responseData.type === "response.function_call_arguments.done" && responseData.name === "update_scores" && responseData.arguments) {
        try {
          const args = JSON.parse(responseData.arguments);
          if (Array.isArray(args.scores)) {
            gameState.updateScores(args.scores);
            //console.log("🎯 Player scores updated from function call");
          }
        } catch (err) {
          console.error("❌ Failed to parse function arguments:", err);
        }
      }

      // Handle function calls for displaying the question
      if (responseData.type === "response.function_call_arguments.done" && responseData.name === "display_question" && responseData.arguments) {
        try {
          const args = JSON.parse(responseData.arguments);
          if (args.question && typeof args.round === 'number') {
            gameState.displayQuestion(args.question, args.round);
            //console.log(`📝 Displaying question for round ${args.round}:`, args.question);
          }
        } catch (err) {
          console.error("❌ Failed to parse display_question arguments:", err);
        }
      }

      // Handle function calls for ending the game — defer actual teardown
      if (responseData.type === "response.function_call_arguments.done" && responseData.name === "end_game") {
        console.log("🏁 AI requested end_game — will defer teardown until response completes");
        pendingEndGameRef.current = true;
        // Do not teardown immediately; wait for response.done to fire, then pause 3s
      }
    } catch (error) {
      console.error("Error processing AI response:", error);
    }
  };

  // Initialize useWebRTC hook
  const webRTC = useWebRTC({
    conversation: gameState.conversation,
    conversationMode: userPreferences.conversationMode,
    audioMode: userPreferences.audioMode,
    onMessageReceived: enhancedHandleWebRTCMessage,
    setIsTalking: () => { },
    setMicVolume: setMicVolume,
    setRemoteAudioVolume: setRemoteAudioVolume,
    onUsageUpdate: gameState.handleUsageUpdate,
    onGuardrailEvent: async (evt) => {
      try {
        if (evt?.type === 'session_ended' && evt.reason === 'token_limit') {
          // Wait for final audio to finish: min 2s, attempt to detect quiet, cap 5s
          const MIN_MS = 2000;
          const CAP_MS = 5000;
          const QUIET_THRESHOLD = 6; // analyzer avg ~0-255
          const QUIET_STREAK_MS = 600;
          const start = Date.now();
          let quietStart = 0;

          const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
          // Ensure at least MIN_MS passes
          while (Date.now() - start < MIN_MS) {
            await sleep(100);
          }
          // Then wait for quiet or until CAP_MS
          while (Date.now() - start < CAP_MS) {
            if (remoteAudioVolumeRef.current <= QUIET_THRESHOLD) {
              if (quietStart === 0) quietStart = Date.now();
              if (Date.now() - quietStart >= QUIET_STREAK_MS) break;
            } else {
              quietStart = 0;
            }
            await sleep(120);
          }

          await gameState.endConversation({ skipFinalize: true });
          const aiCount = typeof webRTC.getAiReplyCount === 'function' ? webRTC.getAiReplyCount() : 0;
          await gameState.finalizeGameSession('token_limit', aiCount);
        }
      } catch (e) {
        console.warn('Guardrail finalization failed:', e);
      }
    }
  });
  const webRTCRef = useRef(webRTC);
  useEffect(() => {
    webRTCRef.current = webRTC;
  }, [webRTC]);

  const {
    canUseNativePrompt,
    promptInstall,
  } = usePwaInstallPrompt();

  const maybeQueueInstallPrompt = useCallback(() => {
    if (embedded || !shouldShowInstallPrompt()) {
      return;
    }

    const variant = getInstallPromptVariant(canUseNativePrompt);

    if (!variant) {
      return;
    }

    setInstallPromptVariant(variant);
    setInstallPromptQueued(true);
  }, [canUseNativePrompt, embedded]);

  useEffect(() => {
    maybeQueueInstallPromptRef.current = maybeQueueInstallPrompt;
  }, [maybeQueueInstallPrompt]);

  const showQueuedInstallPrompt = useCallback(() => {
    if (!installPromptQueued || !installPromptVariant) {
      return;
    }

    markInstallPromptShown();
    setInstallPromptQueued(false);
  }, [installPromptQueued, installPromptVariant]);

  const closeInstallPrompt = useCallback(() => {
    setInstallPromptVariant(null);
    setInstallPromptQueued(false);
  }, []);

  const handleInstallPromptDismiss = useCallback(() => {
    markInstallPromptDismissed();
    closeInstallPrompt();
  }, [closeInstallPrompt]);

  const handleInstallPromptMarkedInstalled = useCallback(() => {
    markInstallPromptInstalled();
    closeInstallPrompt();
  }, [closeInstallPrompt]);

  const handleInstallPromptClick = useCallback(async () => {
    const result = await promptInstall();

    if (result?.outcome === 'accepted') {
      closeInstallPrompt();
      return;
    }

    markInstallPromptDismissed();
    closeInstallPrompt();
  }, [closeInstallPrompt, promptInstall]);

  const handleFinalResultsClose = useCallback(() => {
    gameState.closeFinalResults();
    showQueuedInstallPrompt();
  }, [gameState, showQueuedInstallPrompt]);

  useEffect(() => {
    if (
      embedded ||
      activeSessionRestoreAttemptedRef.current ||
      gameState.conversationStarted ||
      gameState.loading ||
      !authenticatedUser
    ) {
      return;
    }

    const snapshot = readActiveGameSessionSnapshot();
    if (!snapshot) {
      activeSessionRestoreAttemptedRef.current = true;
      return;
    }

    activeSessionRestoreAttemptedRef.current = true;

    if (snapshot.conversationMode && snapshot.conversationMode !== conversationMode) {
      setConversationMode(snapshot.conversationMode);
    }

    gameState.resumeActiveSessionFromSnapshot(snapshot, webRTC.reconnectWithSessionFromArgs)
      .catch((error) => {
        console.error('Active game resume failed:', error);
      });
  }, [
    authenticatedUser,
    conversationMode,
    embedded,
    gameState,
    setConversationMode,
    webRTC.reconnectWithSessionFromArgs,
  ]);

  const attemptLifecycleResume = useCallback(async (reason = 'lifecycle') => {
    if (
      !gameState.conversationStarted ||
      lifecycleResumeInFlightRef.current ||
      gameState.activeSessionRestore?.status === 'resuming'
    ) {
      return;
    }

    const isConnectionOpen = typeof webRTC.isRealtimeConnectionOpen === 'function'
      ? webRTC.isRealtimeConnectionOpen()
      : webRTC.dataChannel?.readyState === 'open';

    if (isConnectionOpen) {
      return;
    }

    const snapshot = readActiveGameSessionSnapshot();
    if (!snapshot) {
      return;
    }

    lifecycleResumeInFlightRef.current = true;
    try {
      console.info(`Realtime connection is not open after ${reason}; attempting game resume.`);
      clearIdleTimers();
      setIdleWarningVisible(false);
      setIdleCountdownSeconds(0);
      await gameState.resumeActiveSessionFromSnapshot(snapshot, webRTC.reconnectWithSessionFromArgs);
    } finally {
      lifecycleResumeInFlightRef.current = false;
    }
  }, [
    clearIdleTimers,
    gameState,
    webRTC,
  ]);

  // Start conversation function that calls WebRTC initialization
  const handleStartConversation = useCallback(async () => {
    console.log("🚀 Starting conversation...");
    await gameState.startConversation(webRTC.initializeWebRTCConnection);
  }, [gameState, webRTC.initializeWebRTCConnection]);

  // Initialize audio controls hook
  const audioControls = useAudioControls({
    audioMode: userPreferences.audioMode,
    conversationStarted: gameState.conversationStarted,
    localStreamRef: webRTC.localStreamRef,
    setWebRTCMuted: webRTC.setMuted,
    setIsTalking: () => { },
    micVolume: micVolume
  });

  // Handle end game confirmation
  const handleEndGameConfirm = async () => {
    clearIdleTimers();
    setIdleWarningVisible(false);
    setIdleCountdownSeconds(0);

    // Cancel any pending AI-triggered deferred teardown to avoid double cleanup
    if (endGameTimerRef.current) {
      clearTimeout(endGameTimerRef.current);
      endGameTimerRef.current = null;
    }
    pendingEndGameRef.current = false;

    setShowConfirmDialog(false);
    await webRTCRef.current?.cleanup?.();
    await gameState.endConversation({ skipFinalize: true });
    try {
      const aiCount = typeof webRTC.getAiReplyCount === 'function' ? webRTC.getAiReplyCount() : 0;
      await gameState.finalizeGameSession('user_ended', aiCount);
    } catch (e) { /* ignore */ }
    if (embedded && typeof onEmbeddedSessionClosed === 'function') {
      onEmbeddedSessionClosed();
    }
  };

  const handleEndGameCancel = () => {
    setShowConfirmDialog(false);
  };

  useEffect(() => {
    if (typeof window === 'undefined' || embedded || !gameState.conversationStarted) {
      return undefined;
    }

    if (window.history.state?.cqActiveGame) {
      return undefined;
    }

    window.history.pushState(
      {
        ...(window.history.state || {}),
        cqActiveGame: true,
      },
      document.title,
      window.location.href
    );

    return undefined;
  }, [embedded, gameState.conversationStarted]);

  useEffect(() => {
    if (typeof window === 'undefined' || embedded) {
      return undefined;
    }

    const handleDeviceBack = () => {
      if (!conversationStartedRef.current) {
        return;
      }

      if (showConfirmDialogRef.current) {
        setShowConfirmDialog(false);
      } else {
        setShowConfirmDialog(true);
      }

      window.history.pushState(
        {
          ...(window.history.state || {}),
          cqActiveGame: true,
        },
        document.title,
        window.location.href
      );
    };

    window.addEventListener('popstate', handleDeviceBack);
    return () => {
      window.removeEventListener('popstate', handleDeviceBack);
    };
  }, [embedded]);

  // Handle settings
  const handleSettingsClose = () => {
    setShowSettings(false);
  };

  const handleSettingsUpdate = async () => {
    await userPreferences.updateUserPreferences();
  };

  const handleCreditStoreClose = () => {
    setShowCreditStore(false);
  };

  const handleCreditPurchase = async (purchasableItemId) => {
    await gameState.purchaseCredits(purchasableItemId);
  };

  const handleContentPurchase = async (pack) => {
    if (!pack?.purchasable_item_id) {
      toast.error('This item is not available for purchase yet.');
      return;
    }

    await gameState.purchaseStoreItem(
      pack.purchasable_item_id,
      {
        itemLabel: pack.name || 'this item',
        pendingSelection: {
          type: 'quest_pack',
          gamePackId: gameState.selectedGamePack,
          questPackId: pack.id,
        },
      }
    );
  };

  // Handle conversation updates from ConversationManager
  const handleConversationUpdate = (conversation, methods) => {
    // Store conversation methods for use in other components
    if (methods) {
      // These methods can be used by other components if needed
    }
  };

  // Welcome message when user authenticates
  useEffect(() => {
    if (authenticatedUser?.user?.email) {
      toast.info(`Welcome, ${authenticatedUser.user.email}!`);
    }
  }, [authenticatedUser]);

  useEffect(() => {
    const url = new URL(window.location.href);
    const checkoutStatus = url.searchParams.get('checkout');
    const checkoutSessionId = url.searchParams.get('session_id');

    if (checkoutStatus !== 'success' || !authenticatedUser) {
      return;
    }

    let isActive = true;

    const refreshWalletAfterCheckout = async () => {
      let walletData = null;
      let checkoutConfirmed = false;
      const pendingSelection = readPendingCheckoutSelection();

      try {
        checkoutConfirmed = await confirmCheckoutSession(checkoutSessionId);
      } catch (error) {
        console.error('Error confirming checkout session:', error);
      }

      for (let attempt = 0; attempt < CHECKOUT_WALLET_REFRESH_ATTEMPTS; attempt += 1) {
        if (!isActive) {
          return;
        }

        if (attempt > 0) {
          await wait(CHECKOUT_WALLET_REFRESH_DELAY_MS);
        }

        walletData = await fetchWallet();
      }

      if (!isActive) {
        return;
      }

      url.searchParams.delete('checkout');
      url.searchParams.delete('session_id');
      const nextUrl = `${url.pathname}${url.search}${url.hash}`;
      window.history.replaceState({}, document.title, nextUrl);

      if (
        checkoutConfirmed &&
        pendingSelection?.type === 'quest_pack' &&
        pendingSelection.gamePackId &&
        pendingSelection.questPackId
      ) {
        await setSelectedGamePack(pendingSelection.gamePackId);
        setSelectedPack(pendingSelection.questPackId);
      }

      if (checkoutConfirmed && walletData) {
        toast.success('Purchase complete. Your wallet and library have been refreshed.');
      } else if (walletData) {
        toast.info('Purchase complete. Your library is still updating.');
      } else {
        toast.warning('Purchase complete, but your library could not be refreshed yet.');
      }

      clearPendingCheckoutSelection();
    };

    refreshWalletAfterCheckout();

    return () => {
      isActive = false;
    };
  }, [authenticatedUser, fetchWallet, setSelectedGamePack, setSelectedPack]);

  useEffect(() => {
    if (embedded) {
      creatorStudioLaunchHandledRef.current = '';
      return;
    }

    if (conversationStarted || gameStateLoading) {
      return;
    }

    const url = new URL(window.location.href);
    const creatorStudioParam = url.searchParams.get('creatorStudio');
    const gamePackIdParam = url.searchParams.get('gamePackId');
    const autoStartParam = url.searchParams.get('autoStart');
    const modeParam = url.searchParams.get('mode');
    const launchKey = `${creatorStudioParam || ''}:${gamePackIdParam || ''}:${autoStartParam || ''}:${modeParam || ''}:${location.search}`;

    if (creatorStudioParam !== '1' || !gamePackIdParam) {
      creatorStudioLaunchHandledRef.current = '';
      return;
    }

    if (creatorStudioLaunchHandledRef.current === launchKey) {
      return;
    }

    const selectedPack = gamePacks.find((pack) => pack.id === gamePackIdParam);
    if (!selectedPack) {
      return;
    }

    let cancelled = false;
    creatorStudioLaunchHandledRef.current = launchKey;

    const launchCreatorStudio = async () => {
      if (modeParam === 'text' && conversationMode !== 'text') {
        setConversationMode('text');
      }

      if (selectedGamePack !== gamePackIdParam) {
        await setSelectedGamePack(gamePackIdParam);
      }

      if (cancelled) {
        return;
      }

      if (autoStartParam === '1' && !selectedPack.include_questions) {
        await handleStartConversation();
      }

      url.searchParams.delete('creatorStudio');
      url.searchParams.delete('gamePackId');
      url.searchParams.delete('mode');
      url.searchParams.delete('autoStart');
      window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
    };

    launchCreatorStudio().catch((error) => {
      console.error('Failed to launch Creator Studio from admin handoff:', error);
      toast.error(error.message || 'Unable to launch Creator Studio.');
    });

    return () => {
      cancelled = true;
    };
  }, [
    conversationMode,
    conversationStarted,
    embedded,
    gamePacks,
    gameStateLoading,
    handleStartConversation,
    location.search,
    selectedGamePack,
    setConversationMode,
    setSelectedGamePack,
  ]);

  useEffect(() => {
    if (!embedded || !embeddedLaunchSpec?.gamePackId) {
      return;
    }

    if (conversationStarted || gameStateLoading) {
      return;
    }

    const launchKey = `${embeddedLaunchSpec.gamePackId}:${embeddedLaunchSpec.mode || ''}:${embeddedLaunchSpec.autoStart ? '1' : '0'}`;

    if (creatorStudioLaunchHandledRef.current === launchKey) {
      return;
    }

    const selectedPack = gamePacks.find((pack) => pack.id === embeddedLaunchSpec.gamePackId);
    if (!selectedPack) {
      return;
    }

    let cancelled = false;
    creatorStudioLaunchHandledRef.current = launchKey;

    const launchEmbeddedCreatorStudio = async () => {
      if ((embeddedLaunchSpec.mode || 'text') === 'text' && conversationMode !== 'text') {
        setConversationMode('text');
      }

      if (selectedGamePack !== embeddedLaunchSpec.gamePackId) {
        await setSelectedGamePack(embeddedLaunchSpec.gamePackId);
      }

      if (cancelled) {
        return;
      }

      if (embeddedLaunchSpec.autoStart && !selectedPack.include_questions) {
        await handleStartConversation();
      }
    };

    launchEmbeddedCreatorStudio().catch((error) => {
      console.error('Failed to launch embedded Creator Studio:', error);
      toast.error(error.message || 'Unable to launch Creator Studio.');
      creatorStudioLaunchHandledRef.current = '';
    });

    return () => {
      cancelled = true;
    };
  }, [
    conversationMode,
    conversationStarted,
    embedded,
    embeddedLaunchSpec,
    gamePacks,
    gameStateLoading,
    handleStartConversation,
    selectedGamePack,
    setConversationMode,
    setSelectedGamePack,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      console.log("Cleanup running - component unmounting");
      clearIdleTimers();
      try {
        if (webRTC.localStreamRef.current) {
          webRTC.localStreamRef.current.getTracks().forEach((track) => track.stop());
        }
      } catch (err) {
        console.error("Cleanup error:", err);
      }
    };
  }, [clearIdleTimers, webRTC.localStreamRef]);

  useEffect(() => {
    if (!gameState.conversationStarted) {
      clearIdleTimers();
      setIdleWarningVisible(false);
      setIdleCountdownSeconds(0);
      return;
    }

    scheduleIdleWarning();

    return () => {
      clearIdleTimers();
    };
  }, [clearIdleTimers, gameState.conversationStarted, scheduleIdleWarning]);

  useEffect(() => {
    if (!gameState.conversationStarted || idleWarningVisible) {
      return undefined;
    }

    const handleInteraction = () => {
      recordUserActivity();
    };

    const activityEvents = ['pointerdown', 'keydown', 'touchstart'];
    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, handleInteraction, { passive: true });
    });

    return () => {
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, handleInteraction);
      });
    };
  }, [gameState.conversationStarted, idleWarningVisible, recordUserActivity]);

  useEffect(() => {
    const micIsActive = Number(micVolume) > 6;

    if (gameState.conversationStarted && !idleWarningVisible && micIsActive && !micActivityRef.current) {
      recordUserActivity();
    }

    micActivityRef.current = micIsActive;
  }, [gameState.conversationStarted, idleWarningVisible, micVolume, recordUserActivity]);

  useEffect(() => {
    if (!gameState.conversationStarted || typeof sendActiveGameSessionHeartbeat !== 'function') {
      return undefined;
    }

    const sendHeartbeat = (status = 'active', options = {}) => {
      const currentWebRTC = webRTCRef.current;
      const aiReplyCount = typeof currentWebRTC?.getAiReplyCount === 'function' ? currentWebRTC.getAiReplyCount() : 0;
      sendActiveGameSessionHeartbeat(status, {
        aiReplyCount,
        ...options,
      }).catch((error) => {
        console.warn('Failed to send game session heartbeat:', error);
      });
    };

    const getHeartbeatStatus = () => (
      webRTCRef.current?.connectionStatus === 'reconnecting' ? 'reconnecting' : 'active'
    );

    sendHeartbeat(getHeartbeatStatus());

    const intervalId = window.setInterval(() => {
      sendHeartbeat(getHeartbeatStatus());
    }, 25000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [
    gameState.conversationStarted,
    sendActiveGameSessionHeartbeat,
  ]);

  useEffect(() => {
    if (!gameState.conversationStarted) {
      return undefined;
    }

    const persistActiveState = () => {
      try {
        gameState.persistActiveSessionSnapshot?.();
      } catch (error) {
        console.warn('Failed to persist active session during lifecycle event:', error);
      }
    };

    const pauseForSuspension = () => {
      persistActiveState();
      const currentWebRTC = webRTCRef.current;
      const aiReplyCount = typeof currentWebRTC?.getAiReplyCount === 'function' ? currentWebRTC.getAiReplyCount() : 0;
      sendActiveGameSessionHeartbeat?.('hidden', {
        aiReplyCount,
        keepalive: true,
        useBeacon: true,
      }).catch((error) => {
        console.warn('Failed to send hidden heartbeat:', error);
      });
      clearIdleTimers();
      setIdleWarningVisible(false);
      setIdleCountdownSeconds(0);
    };

    const resumeAfterSuspension = () => {
      scheduleIdleWarning();
      attemptLifecycleResume('page resume');
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        pauseForSuspension();
        return;
      }

      if (document.visibilityState === 'visible') {
        resumeAfterSuspension();
      }
    };

    const handleBeforeUnload = (event) => {
      persistActiveState();
      event.preventDefault();
      event.returnValue = '';
      return '';
    };

    const handleOffline = () => {
      persistActiveState();
      const currentWebRTC = webRTCRef.current;
      const aiReplyCount = typeof currentWebRTC?.getAiReplyCount === 'function' ? currentWebRTC.getAiReplyCount() : 0;
      sendActiveGameSessionHeartbeat?.('reconnecting', {
        aiReplyCount,
        keepalive: true,
        useBeacon: true,
      }).catch((error) => {
        console.warn('Failed to send offline heartbeat:', error);
      });
      clearIdleTimers();
      setIdleWarningVisible(false);
      setIdleCountdownSeconds(0);
      toast.info('Connection lost. Your game state was saved.');
    };

    const handleOnline = () => {
      scheduleIdleWarning();
      attemptLifecycleResume('network reconnect');
    };

    window.addEventListener('pagehide', pauseForSuspension);
    window.addEventListener('pageshow', resumeAfterSuspension);
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('pagehide', pauseForSuspension);
      window.removeEventListener('pageshow', resumeAfterSuspension);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [
    attemptLifecycleResume,
    clearIdleTimers,
    gameState,
    scheduleIdleWarning,
    sendActiveGameSessionHeartbeat,
  ]);

  return (
    <div className={`app-background-wrapper cq-shell${embedded ? ' cq-shell--embedded' : ''}`}>
      <div
        className={`app-background cq-shell__background${embedded ? ' cq-shell__background--embedded' : ''}`}
        style={{ '--cq-bg-artdeco': `url(${process.env.PUBLIC_URL}/images/bg_artdeco_pattern.png)` }}
      >
        <ToastContainer
          position="top-center"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          draggable
          pauseOnHover
          theme="light"
        />

        {/* Loading overlay during game startup */}
        {gameState.loading && (!gameState.conversationStarted || gameState.activeSessionRestore?.status === 'resuming') && (
          <LoadingScreen
            message={gameState.activeSessionRestore?.status === 'resuming'
              ? 'Resuming Cringe Quest...'
              : 'Starting Cringe Quest...'}
            overlay
          />
        )}

        {/* Settings overlay */}
        <SettingsManager
          visible={showSettings}
          onClose={handleSettingsClose}
          preferences={userPreferences}
          onUpdatePreferences={handleSettingsUpdate}
          onSignOut={onSignOut}
          onOpenAdmin={() => navigate('/admin')}
          signOutLoading={signOutLoading}
          isAdmin={isAdmin}
        />

        <CreditStoreModal
          visible={showCreditStore}
          creditBalance={gameState.creditBalance}
          creditBundles={creditBundles}
          loading={Boolean(gameState.purchasingItemId)}
          purchasingItemId={gameState.purchasingItemId}
          onPurchase={handleCreditPurchase}
          onClose={handleCreditStoreClose}
        />

        {/* Confirmation dialog */}
        <ConfirmationDialog
          visible={showConfirmDialog}
          message="Are you sure you want to end the game?"
          confirmText="Yes"
          cancelText="No"
          onConfirm={handleEndGameConfirm}
          onCancel={handleEndGameCancel}
        />

        <IdleWarningModal
          visible={idleWarningVisible}
          countdownSeconds={idleCountdownSeconds}
          onContinue={handleIdleWarningContinue}
        />

        <InstallPromptModal
          isOpen={Boolean(installPromptVariant) && !installPromptQueued}
          variant={installPromptVariant}
          onInstallClick={handleInstallPromptClick}
          onDismiss={handleInstallPromptDismiss}
          onMarkInstalled={handleInstallPromptMarkedInstalled}
        />

        {/* Main container */}
        <div className={showSettings || showCreditStore || gameState.finalResults || idleWarningVisible || (Boolean(installPromptVariant) && !installPromptQueued) || (gameState.loading && !gameState.conversationStarted) ? "blur-background app-container cq-shell__container" : "app-container cq-shell__container"}>
          {gameState.conversationStarted ? (
            <>
              <GameHeader
                title={embedded ? "Creator Studio" : "Cringe Quest"}
                showSubtitle={false}
                titleClassName="cq-selection-header__title cq-game-view__title"
              />

              {userPreferences.conversationMode !== "text" && (
                <PulsatingHead
                  audioVolume={(remoteAudioVolume / 2)}
                  imageSrc={currentHostImageSrc}
                  alt={currentHostName}
                />
              )}
            </>
          ) : (
            <GameSetupScreen
              gamePacks={availableGamePacks}
              selectedGamePack={gameState.selectedGamePack}
              onSelectGamePack={gameState.setSelectedGamePack}
              onClearGamePack={gameState.clearSelectedGamePack}
              selectedGamePackRequiresQuestPack={gameState.selectedGamePackRequiresQuestPack}
              questPacks={gameState.questPackQuestions}
              selectedQuestPack={gameState.selectedPack}
              onSelectQuestPack={gameState.setSelectedPack}
              questPackLoading={gameState.questPackLoading}
              questPackError={gameState.questPackError}
              readyToStart={gameState.readyToStart}
              loading={gameState.loading}
              onProceed={handleStartConversation}
              onOpenSettings={() => setShowSettings(true)}
              onOpenCreditStore={() => setShowCreditStore(true)}
              onPurchaseContent={handleContentPurchase}
              creditBalance={gameState.creditBalance}
              selectedGamePackCost={gameState.selectedGamePackCost}
              projectedRemainingCredits={gameState.projectedRemainingCredits}
              purchasingItemId={gameState.purchasingItemId}
            />
          )}

          {/* Current Question Display */}
          <CurrentQuestion
            question={currentQuestion.question}
            round={currentQuestion.round}
            visible={gameState.conversationStarted && !!currentQuestion.question}
            onHeightChange={setQuestionBoxHeight}
          />

          {/* Conversation Manager */}
          <ConversationManager
            conversation={gameState.conversation}
            onConversationChange={gameState.setConversation}
            conversationMode={userPreferences.conversationMode}
            conversationStarted={gameState.conversationStarted}
            loading={gameState.loading}
            dataChannel={webRTC.dataChannel}
            onMessageReceived={enhancedHandleWebRTCMessage}
            onConversationUpdate={handleConversationUpdate}
            onMessageCountUpdate={gameState.setMessageCount}
            tools={gameState.activeTools}
          />

          {/* Voice mode spacer */}
          {gameState.conversationStarted && userPreferences.conversationMode === "voice" && (
            <div
              className="voice-mode-spacer"
              style={{ height: `${Math.max(20 - questionBoxHeight, 0)}px` }}
            />
          )}

          {gameState.conversationStarted && (
            <div className="controls">
              <AudioControls
                audioMode={userPreferences.audioMode}
                muted={audioControls.muted}
                isTalking={audioControls.isTalking}
                micVolume={audioControls.micVolume}
                onToggleMute={audioControls.toggleMute}
                onPushToTalkStart={audioControls.handlePushToTalkStart}
                onPushToTalkEnd={audioControls.handlePushToTalkEnd}
                conversationStarted={gameState.conversationStarted}
                conversationMode={userPreferences.conversationMode}
              />

              <GameControls
                conversationStarted={gameState.conversationStarted}
                onEndGame={() => setShowConfirmDialog(true)}
              />
            </div>
          )}

          {/* Score Board */}
          {gameState.conversationStarted && userPreferences.showLeaderBoard && (
            <ScoreBoard players={scoreBoardPlayers} />
          )}
        </div>

        {!gameState.conversationStarted && gameState.finalResults && (
          <div className="final-results-overlay" role="dialog" aria-modal="true" aria-label="Final game results">
            <div className="final-results-modal">
              <ScoreBoard
                players={gameState.finalResults.players || {}}
                finalCost={isAdmin ? gameState.finalResults.totalCost : null}
                onClose={handleFinalResultsClose}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatApp;
